import React, { useEffect, useState, lazy, createElement } from 'react';
import { Calendar, Video, CheckCircle2, BookOpen, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '../components/UI/Button';
import { Card } from '../components/UI/Card';
import { PaystackPayment } from '../components/Payment/PaystackPayment';
import { LazySection } from '../components/UI/LazySection';
export const AIClass = () => {
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  // Eagerly load Paystack script when this page mounts
  useEffect(() => {
    if (document.querySelector('script[src*="paystack"]')) return;
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);
  const useCases = ['Drafting professional emails and correspondence', 'Summarizing long documents and reports instantly', 'Creating business plans and proposals', 'Learning new skills and concepts quickly', 'Organizing schedules and daily tasks', 'Generating ideas for marketing and content', 'Translating languages for business or travel', 'Improving writing tone and grammar', 'Analyzing data and creating simple spreadsheets', 'Preparing for interviews or difficult conversations'];
  const curriculum = [{
    title: 'Module 1: The Foundations',
    topics: ['Introduction to AI and ChatGPT', 'The Art of Prompt Engineering', 'Setting up your AI workspace']
  }, {
    title: 'Module 2: Professional Communication',
    topics: ['Writing emails that get responses', 'Adjusting tone for different audiences', 'Drafting reports and summaries']
  }, {
    title: 'Module 3: Daily Productivity',
    topics: ['Automating repetitive tasks', 'Research and information gathering', 'Brainstorming and ideation']
  }, {
    title: 'Module 4: Practical Application',
    topics: ['Live demonstrations', 'Hands-on exercises', 'Interactive Q&A session']
  }];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePaymentSuccess = (reference: string) => {
    // Redirect directly to WhatsApp group after successful payment
    window.location.href = 'https://chat.whatsapp.com/HKWPSPrraU34ebUaCZNBaC?mode=gi_t';
  };
  return <div className="min-h-screen bg-white" data-id="element-1518" data-mp="m44bmit176fp">
      {/* Hero Section */}
      <section className="bg-gray-50 py-16 md:py-24 border-b border-gray-200" data-id="element-1519" data-mp="m5yza4votcnh">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-1520" data-mp="2lfxaym3aknvl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1521" data-mp="2ghdpemsm1e3r">
            <div data-id="element-1522" data-mp="2oyw7qjti2xwt">
              <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-800 font-semibold rounded-full text-sm mb-6" data-id="element-1523" data-mp="2ozfklm4amw3z">
                Virtual Masterclass
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1524" data-mp="2xr7qrkmabj2r">
                Master Everyday AI Skills for Professionals
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1525" data-mp="32rvk4cxzkc05">
                A practical, jargon-free training designed specifically for
                adult learners to integrate AI into their daily work and life.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 mb-8" data-id="element-1526" data-mp="2ezf0vz2iidz9">
                <div className="flex items-center text-gray-700" data-id="element-1527" data-mp="2jz9s63q795ku">
                  <Calendar className="w-6 h-6 mr-3 text-blue-600" data-id="element-1528" data-mp="2jxgisgo7c795" />
                  <div data-id="element-1529" data-mp="3qetwayf2oxs9">
                    <p className="font-semibold" data-id="element-1530" data-mp="3qfdhqp3xm4b1">April 4th, 2024</p>
                    <p className="text-sm text-gray-500" data-id="element-1531" data-mp="3vdnciylqv2w4">Save the date</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-700" data-id="element-1532" data-mp="186slgx6ywuo1">
                  <Video className="w-6 h-6 mr-3 text-blue-600" data-id="element-1533" data-mp="137xuv92re1td" />
                  <div data-id="element-1534" data-mp="139ilinlyd0ly">
                    <p className="font-semibold" data-id="element-1535" data-mp="1h7gqj2edvkos">Google Meet</p>
                    <p className="text-sm text-gray-500" data-id="element-1536" data-mp="1h4kr19mw0ny8">
                      Link provided after registration
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Registration Card */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100" data-id="element-1537" data-mp="1m4z3r4zfom2l">
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1538" data-mp="1zxool6387ky4">
                Secure Your Spot
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-1539" data-mp="1vx4ukbdy8xg7">
                Registration fee:{' '}
                <span className="font-bold text-gray-900" data-id="element-1540" data-mp="29wnw2lc99ajj">₦3,500</span>
              </p>

              <div className="space-y-4 mb-6" data-id="element-1541" data-mp="24xb45m6j1da3">
                <div data-id="element-1542" data-mp="24uf5fvmuukkl">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1543" data-mp="2oikzlb8zznz4">
                    Full Name
                  </label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="John Doe" data-id="element-1544" data-mp="2tg3c1bm84lbv" />
                </div>
                <div data-id="element-1545" data-mp="2akmyv8nlnq23">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1546" data-mp="1vtnwifdcxxqy">
                    Email Address
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="john@example.com" data-id="element-1547" data-mp="20til2c9nub8u" />
                </div>
                <div data-id="element-1548" data-mp="3ooosnz6hyi0l">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1549" data-mp="3jr5cgtq8nv60">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="08012345678" data-id="element-1550" data-mp="3etn5v9zp1s16" />
                </div>
              </div>

              {formData.name && formData.email && formData.phone ? <PaystackPayment amount={3500} email={formData.email} name={formData.name} phone={formData.phone} onSuccess={handlePaymentSuccess} data-id="element-1551" data-mp="2r0n2r9v0evjm" /> : <Button className="w-full opacity-50 cursor-not-allowed" disabled data-id="element-1552" data-mp="16l8h54hg9s5u">
                  Fill details to pay ₦3,500
                </Button>}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section — lazy rendered when scrolled near */}
      <LazySection minHeight="400px" rootMargin="300px" data-id="element-1553" data-mp="1q8fx36q8r5m1">
        <section className="py-16 md:py-24" data-id="element-1554" data-mp="1q6v1rew3rin4">
          <div className="container mx-auto px-4 max-w-6xl" data-id="element-1555" data-mp="1uajc25c4csc2">
            <div className="text-center mb-16" data-id="element-1556" data-mp="1u86uvue6qm02">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1557" data-mp="1z9mgpoo6swji">
                Why You Need This Class
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1558" data-mp="3w3h80a170118">
                10 practical ways you will use AI immediately after this
                training.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-1559" data-mp="26r2km5xtgh25">
              {useCases.map((useCase, index) => <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1560" data-mp="2gif9qps8ct4h" data-mpi={index}>
                  <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1561" data-mp="2bloto2gfdxj2" />
                  <span className="text-gray-700 font-medium" data-id="element-1562" data-mp="1nw3yml6zz9tf">{useCase}</span>
                </div>)}
            </div>
          </div>
        </section>
      </LazySection>

      {/* Curriculum Section — lazy rendered when scrolled near */}
      <LazySection minHeight="400px" rootMargin="300px" data-id="element-1563" data-mp="1ssuou1cinlsk">
        <section className="py-16 md:py-24 bg-gray-50 border-t border-gray-200" data-id="element-1564" data-mp="1svgxk4b587cd">
          <div className="container mx-auto px-4 max-w-6xl" data-id="element-1565" data-mp="395j9af8k0f8v">
            <div className="text-center mb-16" data-id="element-1566" data-mp="3492n3qkx0ayf">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1567" data-mp="34adqhpag7gp0">
                Course Curriculum
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1568" data-mp="v04ihsqvosra">
                A structured, easy-to-follow learning path starting from the
                absolute basics.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1569" data-mp="lzy8igue2awv">
              {curriculum.map((module, index) => <Card key={index} className="p-6 border-t-4 border-t-blue-600" data-id="element-1570" data-mp="19qlv888yr53h" data-mpi={index}>
                  <div className="flex items-center mb-4" data-id="element-1571" data-mp="1dpkwnivhkjq6">
                    <BookOpen className="w-6 h-6 text-blue-600 mr-3" data-id="element-1572" data-mp="zz7q9ky8td2y" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-1573" data-mp="zwv754gr0wob">
                      {module.title}
                    </h3>
                  </div>
                  <ul className="space-y-3" data-id="element-1574" data-mp="1smld2q888s9c">
                    {module.topics.map((topic, tIndex) => <li key={tIndex} className="flex items-start" data-id="element-1575" data-mp="1xfmvv9bk1by7" data-mpi={tIndex}>
                        <ArrowRight className="w-4 h-4 text-gray-400 mr-2 mt-1 flex-shrink-0" data-id="element-1576" data-mp="1p1lks2w7awu6" />
                        <span className="text-gray-600" data-id="element-1577" data-mp="1u1hmtwlc8e73">{topic}</span>
                      </li>)}
                  </ul>
                </Card>)}
            </div>
          </div>
        </section>
      </LazySection>
    </div>;
};